import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { checkPaymentHistory, checkEmailAvailability } from "../../utils/api";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (email: string, password: string) => Promise<void>;
  onContinueWithPayment: (email: string) => Promise<void>;
  loginError: string;
  setShowProfileExistsModal?: (status: boolean) => void;
  setConflictingEmail?: (email: string) => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onLogin,
  onContinueWithPayment,
  loginError,
  setShowProfileExistsModal,
  setConflictingEmail,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {},
  );
  const [showContinueOption, setShowContinueOption] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<{
    hasPaid: boolean;
    emailAvailable: boolean;
  } | null>(null);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setPassword("");
      setErrors({});
      setIsLoading(false);
      setShowContinueOption(false);
      setPaymentStatus(null);
    }
  }, [isOpen]);

  // Check payment status and email availability when email changes
  useEffect(() => {
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      const checkPaymentAndAvailability = async () => {
        try {
          const [paymentResult, emailResult] = await Promise.all([
            checkPaymentHistory(email),
            checkEmailAvailability(email),
          ]);

          const hasPaid =
            paymentResult.has_payments &&
            paymentResult.payments.some(
              (payment) => payment.status === "succeeded"
            );
          const emailAvailable = emailResult.isAvailable;
          setPaymentStatus({ hasPaid, emailAvailable });
          setShowContinueOption(hasPaid && emailAvailable);

          setShowProfileExistsModal?.(true);
          setConflictingEmail?.(email);
        } catch (error) {
          console.error("Error checking payment status:", error);
          setPaymentStatus(null);
          setShowContinueOption(false);
        }
      };

      // Debounce the check
      const timeout = setTimeout(checkPaymentAndAvailability, 1000);
      return () => clearTimeout(timeout);
    } else {
      setPaymentStatus(null);
      setShowContinueOption(false);
    }
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!password.trim()) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await onLogin(email, password);
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueWithPayment = async () => {
    if (!email) return;
    
    setIsLoading(true);
    try {
      await onContinueWithPayment(email);
    } catch (error) {
      console.error("Continue with payment error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="login-modal-overlay"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="login-modal"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {/* Close Button */}
          <button
            className="login-modal-close"
            onClick={onClose}
            aria-label="Close login modal"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Header */}
          <div className="login-modal-header">
            <div className="login-icon">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 17L15 12L10 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 12H3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="login-modal-title">Welcome Back</h2>
            <p className="login-modal-subtitle">
              Sign in to access your existing profile
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="login-form">
            {loginError && (
              <div className="login-form-error text-center">
                <p className="text-red-500 text-sm mb-4">{loginError}</p>
              </div>
            )}
            <div className="login-form-group">
              <label htmlFor="login-email" className="login-form-label">
                Email Address
              </label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`login-form-input ${errors.email ? "error" : ""}`}
                placeholder="Enter your email"
                disabled={isLoading}
              />
              {errors.email && (
                <span className="login-form-error">{errors.email}</span>
              )}
            </div>

            <div className="login-form-group">
              <label htmlFor="login-password" className="login-form-label">
                Password
              </label>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`login-form-input ${errors.password ? "error" : ""}`}
                placeholder="Enter your password"
                disabled={isLoading}
              />
              {errors.password && (
                <span className="login-form-error">{errors.password}</span>
              )}
            </div>

            <button
              type="submit"
              className="login-submit-btn"
              disabled={isLoading}
              onClick={handleSubmit}
            >
              {isLoading ? (
                <div className="login-loading">
                  <div className="login-spinner"></div>
                  Signing In...
                </div>
              ) : (
                "Sign In"
              )}
            </button>

            {/* Continue with Payment Option */}
            {showContinueOption && paymentStatus?.hasPaid && paymentStatus?.emailAvailable && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-center">
                  <div className="text-green-800 font-semibold mb-2">
                    ✓ Payment Detected
                  </div>
                  <p className="text-green-700 text-sm mb-3">
                    We found a completed payment for this email. You can continue creating your profile without paying again.
                  </p>
                  <button
                    type="button"
                    onClick={handleContinueWithPayment}
                    disabled={isLoading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="login-spinner mr-2"></div>
                        Continuing...
                      </div>
                    ) : (
                      "Continue Creating Profile"
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>

          {/* Footer */}
          <div className="login-modal-footer">
            <p className="login-footer-text">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={onClose}
                className="login-footer-link"
              >
                Create one now
              </button>
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
